import { Card, CardBody, CardHeader, Chip, Link, SortDescriptor, Spinner } from '@nextui-org/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '~/components/common/Button'
import { Column, DataTable } from '~/components/common/DataTable'
import { InvoicePaymentRow, getAllInvoices } from '~/services/superAdminService'
import { formatDate } from '~/utils/timezone'
import { BetterToast } from '~/utils/toast'

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className='flex flex-col gap-0.5 px-4 py-3 bg-white rounded-lg border border-tokens-neutral-border-2 min-w-[140px]'>
      <span className='text-[11px] font-medium text-default-400 uppercase tracking-wide'>
        {label}
      </span>
      <span className='text-xl font-bold text-default-900'>{value}</span>
      {sub && <span className='text-[11px] text-default-400'>{sub}</span>}
    </div>
  )
}

const columns: Column[] = [
  { name: 'Date', uid: 'date', sortable: true },
  { name: 'Amount', uid: 'amount', sortable: true },
  { name: 'Status', uid: 'status' },
  { name: 'Invoice', uid: 'invoice' },
  { name: 'Account', uid: 'account', sortable: true },
  { name: 'Type', uid: 'account_type' }
]

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
  paid: 'success',
  open: 'warning',
  void: 'default',
  uncollectible: 'danger',
  draft: 'default'
}

const TYPE_COLORS: Record<
  string,
  'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
> = {
  flex: 'default',
  pro: 'primary',
  team: 'secondary',
  enterprise: 'success',
  enterprise_migrate: 'success',
  enterprise_migrate_assess: 'success',
  enterprise_migrate_accelerate: 'success',
  enterprise_migrate_refactor: 'success'
}

function formatCents(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2
  }).format(cents / 100)
}

export default function BillingPaymentsOverview() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const [allInvoices, setAllInvoices] = useState<InvoicePaymentRow[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const [filterValue, setFilterValue] = useState(() => searchParams.get('filter') || '')
  const [page, setPage] = useState(() => {
    const p = searchParams.get('page')
    return p ? parseInt(p, 10) : 1
  })
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const r = searchParams.get('rows')
    return r ? parseInt(r, 10) : 50
  })
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>(() => ({
    column: searchParams.get('sort') || 'date',
    direction: (searchParams.get('dir') as 'ascending' | 'descending') || 'descending'
  }))

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await getAllInvoices()
      setAllInvoices(data)
    } catch {
      BetterToast.error('Failed to fetch invoices')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const params = new URLSearchParams()
    if (filterValue) params.set('filter', filterValue)
    if (page !== 1) params.set('page', String(page))
    if (rowsPerPage !== 50) params.set('rows', String(rowsPerPage))
    if (sortDescriptor.column && sortDescriptor.column !== 'date') {
      params.set('sort', String(sortDescriptor.column))
    }
    if (sortDescriptor.direction && sortDescriptor.direction !== 'descending') {
      params.set('dir', sortDescriptor.direction)
    }
    setSearchParams(params, { replace: true })
  }, [filterValue, page, rowsPerPage, sortDescriptor, setSearchParams])

  const stats = useMemo(() => {
    const totalPaid = allInvoices.reduce((s, r) => s + r.amount_paid, 0)
    const paidCount = allInvoices.filter(r => r.status === 'paid').length
    const openCount = allInvoices.filter(r => r.status === 'open').length
    const uniqueAccounts = new Set(allInvoices.map(r => r.billing_id)).size
    return { total: allInvoices.length, totalPaid, paidCount, openCount, uniqueAccounts }
  }, [allInvoices])

  const filteredItems = useMemo(() => {
    if (!filterValue) return allInvoices
    const lower = filterValue.toLowerCase()
    return allInvoices.filter(r =>
      [
        r.invoice_id,
        r.invoice_number,
        r.billing_id?.toString(),
        r.billing_name,
        r.billing_email,
        r.billing_to,
        r.account_type,
        r.stripe_customer_id,
        r.description,
        r.status
      ].some(v => v?.toLowerCase().includes(lower))
    )
  }, [allInvoices, filterValue])

  const sortedItems = useMemo(() => {
    const col = sortDescriptor.column as string
    const dir = sortDescriptor.direction === 'ascending' ? 1 : -1

    return [...filteredItems].sort((a, b) => {
      let aV: string | number = 0
      let bV: string | number = 0

      switch (col) {
        case 'date':
          aV = a.created_at ?? ''
          bV = b.created_at ?? ''
          break
        case 'amount':
          aV = a.amount_paid
          bV = b.amount_paid
          break
        case 'account':
          aV = a.billing_name?.toLowerCase() ?? ''
          bV = b.billing_name?.toLowerCase() ?? ''
          break
        default:
          aV = a.created_at ?? ''
          bV = b.created_at ?? ''
      }

      if (aV < bV) return -dir
      if (aV > bV) return dir
      return 0
    })
  }, [filteredItems, sortDescriptor])

  const pages = Math.max(1, Math.ceil(sortedItems.length / rowsPerPage))
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * rowsPerPage
    return sortedItems.slice(start, start + rowsPerPage)
  }, [page, sortedItems, rowsPerPage])

  const tableItems = useMemo(
    () => paginatedItems.map((r, i) => ({ ...r, id: `${r.invoice_id}-${i}` })),
    [paginatedItems]
  )

  const navigateToAccount = useCallback(
    (billingId: number) => {
      navigate(`/super-admin/account-management?filter=${billingId}`)
    },
    [navigate]
  )

  const handleCopy = useCallback(async () => {
    const h = [
      'Date',
      'Amount Paid',
      'Amount Due',
      'Currency',
      'Status',
      'Invoice #',
      'Invoice ID',
      'Billing ID',
      'Account Name',
      'Account Email',
      'Billing To',
      'Type',
      'Stripe Customer',
      'Description',
      'Paid At',
      'Due Date',
      'Invoice URL'
    ]
    const d = filteredItems.map(r => [
      r.created_at ?? '',
      formatCents(r.amount_paid, r.currency),
      formatCents(r.amount_due, r.currency),
      r.currency,
      r.status ?? '',
      r.invoice_number ?? '',
      r.invoice_id,
      r.billing_id ?? '',
      r.billing_name ?? '',
      r.billing_email ?? '',
      r.billing_to ?? '',
      r.account_type ?? '',
      r.stripe_customer_id ?? '',
      r.description ?? '',
      r.paid_at ?? '',
      r.due_date ?? '',
      r.hosted_invoice_url ?? ''
    ])
    const tsv = [h.join('\t'), ...d.map(row => row.join('\t'))].join('\n')
    try {
      await navigator.clipboard.writeText(tsv)
      BetterToast.success(`Copied ${filteredItems.length} rows to clipboard`)
    } catch {
      BetterToast.error('Failed to copy to clipboard')
    }
  }, [filteredItems])

  const renderCell = (item: InvoicePaymentRow & { id: string }, columnKey: string) => {
    switch (columnKey) {
      case 'date':
        return (
          <div className='flex flex-col gap-0.5 min-w-[90px]'>
            <span className='text-xs font-medium'>
              {item.created_at ? formatDate(item.created_at) : '-'}
            </span>
            {item.paid_at && (
              <span className='text-[10px] text-default-400'>Paid {formatDate(item.paid_at)}</span>
            )}
          </div>
        )

      case 'amount':
        return (
          <div className='flex flex-col gap-0.5 items-end min-w-[80px]'>
            <span className='text-sm font-bold text-default-900'>
              {formatCents(item.amount_paid, item.currency)}
            </span>
            {item.amount_due !== item.amount_paid && item.amount_due > 0 && (
              <span className='text-[10px] text-default-400'>
                Due: {formatCents(item.amount_due, item.currency)}
              </span>
            )}
          </div>
        )

      case 'status':
        return (
          <div className='flex flex-col gap-1'>
            <Chip
              size='sm'
              variant='flat'
              color={STATUS_COLORS[item.status ?? ''] ?? 'default'}
              className='text-[10px] h-5'
            >
              {item.status ?? 'unknown'}
            </Chip>
            {item.invoice_type !== 'subscription' && (
              <Chip size='sm' variant='flat' color='secondary' className='text-[9px] h-4'>
                {item.invoice_type}
              </Chip>
            )}
          </div>
        )

      case 'invoice': {
        const label = item.invoice_number ?? item.invoice_id.slice(0, 16)
        return (
          <div className='flex flex-col gap-0.5 min-w-[100px]'>
            {item.hosted_invoice_url ? (
              <Link
                href={item.hosted_invoice_url}
                isExternal
                size='sm'
                className='text-xs font-mono'
              >
                {label}
              </Link>
            ) : (
              <span className='text-xs font-mono'>{label}</span>
            )}
            {item.description && (
              <span className='text-[10px] text-default-400 truncate max-w-[200px]'>
                {item.description}
              </span>
            )}
            {item.pdf_url && (
              <Link href={item.pdf_url} isExternal size='sm' className='text-[10px]'>
                PDF
              </Link>
            )}
          </div>
        )
      }

      case 'account':
        return (
          <div className='flex flex-col gap-0.5 min-w-[130px]'>
            <button
              className='flex items-center gap-2 text-left cursor-pointer hover:opacity-70 transition-opacity'
              onClick={() => navigateToAccount(item.billing_id)}
            >
              <span className='text-xs font-semibold text-primary truncate max-w-[160px] underline'>
                {item.billing_name || '-'}
              </span>
              <span className='text-[10px] font-mono text-default-300'>#{item.billing_id}</span>
            </button>
            <span className='text-[10px] text-default-400 truncate max-w-[180px]'>
              {item.billing_email || '-'}
            </span>
          </div>
        )

      case 'account_type':
        return (
          <Chip
            size='sm'
            variant='flat'
            color={TYPE_COLORS[item.account_type ?? ''] ?? 'default'}
            className='text-[10px]'
          >
            {item.account_type ?? 'flex'}
          </Chip>
        )

      default:
        return null
    }
  }

  return (
    <div className='flex flex-col h-[calc(100vh-160px)] overflow-hidden'>
      <div className='flex gap-3 flex-wrap shrink-0 pb-4'>
        <StatCard label='Total Invoices' value={stats.total} />
        <StatCard label='Total Paid' value={formatCents(stats.totalPaid)} />
        <StatCard label='Paid' value={stats.paidCount} sub='invoices' />
        {stats.openCount > 0 && <StatCard label='Open' value={stats.openCount} sub='unpaid' />}
        <StatCard label='Accounts' value={stats.uniqueAccounts} sub='unique billing accounts' />
      </div>

      <Card className='flex-1 min-h-0 flex flex-col overflow-hidden'>
        <CardHeader className='flex justify-between items-center shrink-0'>
          <div className='flex items-center gap-3'>
            <h2 className='text-xl font-bold'>Invoice Payments</h2>
            {!isLoading && (
              <span className='text-xs text-default-400'>
                {stats.total} invoices, sorted by date desc
              </span>
            )}
          </div>
          <div className='flex items-center gap-3'>
            <Button variant='outlined' className='h-8 text-sm' onClick={handleCopy}>
              Copy as TSV
            </Button>
            <Button variant='outlined' className='h-8 text-sm' onClick={fetchData}>
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardBody className='flex-1 min-h-0 overflow-auto p-0 px-3 pb-3'>
          {isLoading ? (
            <div className='flex flex-col items-center justify-center py-16 gap-3'>
              <Spinner size='lg' />
              <span className='text-sm text-default-400'>Fetching invoices from Stripe...</span>
            </div>
          ) : (
            <DataTable
              items={tableItems}
              columns={columns}
              page={page}
              pages={pages}
              rowsPerPage={rowsPerPage}
              filterValue={filterValue}
              sortDescriptor={sortDescriptor}
              isLoading={false}
              onSearchChange={v => {
                setFilterValue(v)
                setPage(1)
              }}
              onClear={() => {
                setFilterValue('')
                setPage(1)
              }}
              onRowsPerPageChange={setRowsPerPage}
              onPageChange={setPage}
              onSortChange={setSortDescriptor}
              renderCell={renderCell}
              searchPlaceholder='Search by name, email, invoice #, stripe ID, status...'
              totalCount={filteredItems.length}
            />
          )}
        </CardBody>
      </Card>
    </div>
  )
}
